import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { closeMenuOnScroll } from "Pages/Blood/BloodCompendium/Test/Shared";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import NoRecord from "Shared/Common/NoRecord";
import useLang from "Shared/hooks/useLanguage";
import { reactSelectStyle, styles } from "Utils/Common";

interface SpecimenType {
  specimenTypeId: number;
  specimenTypeName: string;
}
interface DataRecollect {
  isOrderCreate: boolean;
  specimenTypes: SpecimenType[];
  tests: Test[];
}

interface Test {
  requisitionTestID: number;
  testId: number;
  testName: string;
  specimenType: string;
  rejectReasonId: number | null;
}

interface Option {
  value: number;
  label: string;
}

interface CommonRecollectModalProps {
  RequisitionOrderId: number;
  RequisitionId: number;
  setOpenRecollect: (value: boolean) => void; // you can type this better if needed
  openRecollect: boolean;
  loadGridData: any;
  headerName?: string;
}

const CommonRecollectModal: React.FC<CommonRecollectModalProps> = ({
  RequisitionOrderId,
  RequisitionId,
  setOpenRecollect,
  openRecollect,
  loadGridData,
  headerName,
}) => {
  const { t } = useLang();

  const initialRecollectValues = {
    requisitionOrderId: RequisitionOrderId,
    requisitionId: RequisitionId,
    specimenTypeIds: [] as number[],
    cancellationReason: "",
    cancellationComment: "",
    cancellationDate: "",
    isOrderCreate: false,
    tests: [
      {
        requisitionTestID: 0,
        testId: 0,
        testName: "",
        specimenType: "",
        rejectReasonId: 0,
      },
    ],
  };

  const [recollectValues, setRecollectValues] = useState(
    initialRecollectValues
  );
  const [dataRecollect, setDataRecollect] = useState<DataRecollect | undefined>(
    undefined
  );
  const [rejectedReasonLookup, setRejectedReasonLookup] = useState<Option[]>(
    []
  );

  const getRejectReasonTypesLookup = async () => {
    const response = await RequisitionType.getPartialRejectionLookup();
    setRejectedReasonLookup(response?.data);
  };

  const getReCollectData = async (specimenTypeIds: number[] = []) => {
    const response = await RequisitionType.GetReCollectData({
      reqOrderId: RequisitionOrderId,
      specimenTypeIds: specimenTypeIds,
    });
    if (response?.data?.statusCode < 300) {
      setDataRecollect(response?.data?.data);
      setRecollectValues((prev: any) => ({
        ...prev,
        isOrderCreate: response?.data?.data?.isOrderCreate,
        tests: response?.data?.data?.tests,
      }));
    }
  };

  const handleChangeChecked = (
    value: number,
    checked: boolean,
    name?: string
  ) => {
    setRecollectValues((prev: any) => {
      if (name === "isOrderCreate") {
        return { ...prev, isOrderCreate: checked };
      }
      const updatedSpecimenTypeIds = checked
        ? [...prev.specimenTypeIds, value]
        : prev.specimenTypeIds.filter((id: number) => id !== value);

      getReCollectData(updatedSpecimenTypeIds);

      return { ...prev, specimenTypeIds: updatedSpecimenTypeIds };
    });
  };

  const handleCloseReCollect = () => {
    setOpenRecollect(false);
    setRecollectValues(initialRecollectValues);
    setDataRecollect(undefined);
  };

  const handleChange = (name: string, value: any) => {
    if (name === "cancellationReasonAll") {
      setRecollectValues((prev: any) => ({
        ...prev,
        tests: prev?.tests?.map((test: any) => ({
          ...test,
          rejectReasonId: value,
        })),
      }));
    } else {
      setRecollectValues((prev: any) => {
        return { ...prev, [name]: value };
      });
    }
  };

  const handleChangeSelect = (name: string, value: any, testId: number) => {
    setRecollectValues((prev: any) => ({
      ...prev,
      tests: prev.tests.map((test: any) =>
        test.testId === testId
          ? {
              ...test,
              rejectReasonId: value,
            }
          : test
      ),
    }));
  };

  const saveReCollect = async () => {
    if (recollectValues.specimenTypeIds.length === 0) {
      toast.error(t("Please select at least one collected specimen."));
      return;
    }
    if (recollectValues.tests.length === 0) {
      toast.error(t("There is not test to recollect."));
      return;
    }

    if (recollectValues.cancellationDate === "") {
      toast.error(t("Please enter a valid recollect date."));
      return;
    }

    const rejectedReasonForAll = recollectValues.tests.filter(
      (rejected) => rejected.rejectReasonId === null
    );
    if (rejectedReasonForAll.length > 0) {
      toast.error(t("Please select a rejection reason for all tests."));
      return;
    }
    const payLoad = {
      requisitionId: recollectValues.requisitionId,
      requisitionOrderId: recollectValues.requisitionOrderId,
      cancellationReason: recollectValues.cancellationReason,
      cancellationComment: recollectValues.cancellationComment,
      reCollectDate: recollectValues.cancellationDate,
      isOrderCreate: recollectValues.isOrderCreate,
      specimen: dataRecollect?.specimenTypes.map((specimenType: any) => ({
        specimenId: specimenType.specimenTypeId,
        specimenName: specimenType.specimenTypeName,
        isCollected: recollectValues.specimenTypeIds.includes(
          specimenType.specimenTypeId
        ),
      })),
      test: recollectValues.tests.map((test: any) => ({
        testId: test.testId,
        rejectReasonId: test.rejectReasonId,
      })),
    };

    try {
      const response = await RequisitionType.saveReCollect(payLoad);
      if (response?.data?.httpStatusCode === 200) {
        toast.success(t(response.data?.message));
        handleCloseReCollect();
        loadGridData(true, false);
      } else {
        toast.error(t(response.data?.message));
      }
    } catch (error) {
      console.error("Error saving Re-Draw:", error);
    }
  };

  useEffect(() => {
    if (openRecollect) {
      getReCollectData();
      getRejectReasonTypesLookup();
    }
  }, [openRecollect]);

  return (
    <Modal
      show={openRecollect}
      onHide={handleCloseReCollect}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton className="m-0 p-5">
        <h4>{headerName ? headerName : t("Re-Collect Sample")}</h4>
      </Modal.Header>
      <Modal.Body>
        <div style={{ backgroundColor: "#FAFAD2" }} className="rounded">
          <p className="px-4 py-2 fw-bold">
            {t(
              "Note: Please select the specimen(s) you have collected and leave the specimen(s) you have NOT collected blank."
            )}
          </p>
        </div>
        <p className="fw-bold">{t("Specimen Type")}</p>
        <div className="d-flex align-items-center gap-4 justify-content-start flex-wrap mb-5">
          {/* <label className="d-flex justify-content-start align-items-start flex-wrap gap-3"> */}
          {dataRecollect?.specimenTypes.map((specimenType: any) => (
            <div key={specimenType.specimenTypeId}>
              <label className="form-check form-check-sm form-check-solid d-flex justify-content-center">
                <input
                  className="form-check-input"
                  type="checkbox"
                  value={specimenType.specimenTypeId}
                  checked={recollectValues.specimenTypeIds.includes(
                    specimenType.specimenTypeId
                  )}
                  name="specimenTypeId"
                  onChange={(e: any) =>
                    handleChangeChecked(+e.target.value, e.target.checked)
                  }
                />
                <span className="ps-2 mr-2">
                  {specimenType.specimenTypeName}
                </span>
              </label>
            </div>
          ))}
          {/* </label> */}
        </div>
        <p className="fw-bold">{t("Cancellation Reason For All")}</p>
        <div className="d-flex align-items-center gap-4 justify-content-start flex-wrap mb-5">
          <Select
            menuPortalTarget={document.body}
            theme={(theme: any) => styles(theme)}
            options={rejectedReasonLookup}
            styles={reactSelectStyle}
            name="cancellationReasonAll"
            placeholder={t("--- Select ---")}
            onChange={(event: any) =>
              handleChange("cancellationReasonAll", event.value)
            }
            isSearchable={true}
            className="w-100"
          />
        </div>
        <p className="fw-bold">{t("Add Comments")}</p>
        <div className="d-flex align-items-center gap-4 justify-content-start flex-wrap mb-5">
          <input
            type="text"
            className="form-control bg-transparent"
            name="cancellationComment"
            value={recollectValues.cancellationComment}
            onChange={(event: any) =>
              handleChange("cancellationComment", event.target.value)
            }
          />
        </div>
        {dataRecollect?.isOrderCreate && (
          <div>
            <label className="mb-5 form-check form-check-sm form-check-solid">
              <input
                className="form-check-input"
                type="checkbox"
                // value={dataRecollect?.isOrderCreate}
                checked={recollectValues.isOrderCreate}
                name="isOrderCreate"
                onChange={(e: any) =>
                  handleChangeChecked(
                    +e.target.value,
                    e.target.checked,
                    "isOrderCreate"
                  )
                }
              />
              <span className="ps-2 mr-2">
                Do you want to create a New Test Order?
              </span>
            </label>
          </div>
        )}
        <p className="fw-bold">{t("Select Date")}</p>
        <div className="d-flex align-items-center gap-4 justify-content-start flex-wrap mb-5">
          <input
            type="date"
            className="form-control bg-transparent"
            name="cancellationDate"
            value={recollectValues.cancellationDate}
            min={new Date().toISOString().split("T")[0]}
            onChange={(event: any) =>
              handleChange("cancellationDate", event.target.value)
            }
          />
        </div>
        <div className="align-items-center card-header d-flex justify-content-center justify-content-sm-between gap-1 mt-5 pb-5">
          <Box sx={{ height: "auto", width: "100%" }}>
            <div className="table_bordered overflow-hidden">
              <TableContainer
                sx={{
                  maxHeight: "calc(100vh - 100px)",
                  "&::-webkit-scrollbar": {
                    width: 7,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#fff",
                  },
                  "&:hover": {
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "var(--kt-gray-400)",
                      borderRadius: 2,
                    },
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "var(--kt-gray-400)",
                    borderRadius: 2,
                  },
                }}
                className="shadow-none"
              >
                <Table
                  aria-label="sticky table collapsible"
                  className="table table-cutome-expend table-bordered table-sticky-header plate-mapping-table table-bg table-head-custom table-vertical-center border-0 mb-0 "
                >
                  <TableHead style={{ zIndex: 10000 }}>
                    <TableRow className="h-40px">
                      <TableCell className="w-50">{t("Test Name")}</TableCell>
                      <TableCell>{t("Specimen Type")}</TableCell>
                      <TableCell>{t("Rejected Reason")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataRecollect?.tests?.length ? (
                      recollectValues?.tests?.map((item: any) => (
                        <TableRow
                          className="h-40px"
                          key={item?.requisitionTestID}
                        >
                          <TableCell>{item?.testName}</TableCell>
                          <TableCell>{item?.specimenTypeName}</TableCell>
                          <TableCell>
                            <Select
                              menuPortalTarget={document.body}
                              theme={(theme: any) => styles(theme)}
                              options={rejectedReasonLookup}
                              styles={reactSelectStyle}
                              name="rejectReasonId"
                              placeholder="--- Select ---"
                              onChange={(event: any) =>
                                handleChangeSelect(
                                  "rejectReasonId",
                                  event.value,
                                  item?.testId
                                )
                              }
                              value={rejectedReasonLookup.filter(
                                (rejected: any) =>
                                  rejected.value === item.rejectReasonId
                              )}
                              closeMenuOnScroll={(e) => closeMenuOnScroll(e)}
                              isSearchable={true}
                              className="w-100"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <NoRecord />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </Box>
        </div>
      </Modal.Body>
      <Modal.Footer className="p-0">
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleCloseReCollect}
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary m-2"
          onClick={() => {
            saveReCollect();
          }}
        >
          {t("Save")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommonRecollectModal;
